import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "../App";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, MapPin, ChevronRight, Zap, Share2, Lock } from "lucide-react";
import { QuickAppointmentButtons } from "./QuickAppointmentCreate";

const API = "/api";

function relativeDate(dateStr) {
  if (!dateStr) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / 86400000);
  if (diff === 0) return "Heute";
  if (diff === 1) return "Morgen";
  const days = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  const dayName = days[target.getDay()];
  return `${dayName}, ${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}`;
}

export default function UpcomingAppointmentsWidget() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: appointments = [], isLoading, isError } = useQuery({
    queryKey: ["upcoming-appointments"],
    queryFn: async () => {
      const res = await authFetch(`${API}/appointments?view=upcoming`);
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 60000,
  });

  if (isError) return null;

  return (
    <div
      data-testid="upcoming-appointments-widget"
      className="bg-white border border-gray-200 mb-3"
      style={{ borderRadius: 2 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[13px] font-semibold text-gray-800">
              Meine Termine
            </span>
            {!isLoading && (
              <span className="text-[10px] text-gray-400 font-normal">
                {appointments.length === 0 ? "Keine" : appointments.length}
              </span>
            )}
          </div>
          <QuickAppointmentButtons
            onCreated={() =>
              queryClient.invalidateQueries({ queryKey: ["upcoming-appointments"] })
            }
          />
        </div>
        <button
          onClick={() => navigate("/app/terminplaner")}
          className="flex items-center gap-0.5 text-[11px] text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="open-terminplaner-btn"
        >
          Terminplaner
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Content */}
      <div className="px-3 py-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="w-4 h-4 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
          </div>
        ) : appointments.length === 0 ? (
          <p className="text-[12px] text-gray-400 py-3 text-center" data-testid="no-appointments-msg">
            Keine anstehenden Termine
          </p>
        ) : (
          <div className="space-y-1" data-testid="appointments-list">
            {appointments.map((appt) => {
              const dateLabel = relativeDate(appt.date);
              const isQuick = appt.is_quick;
              const isShared = appt.visibility === "geteilt";
              const isToday = dateLabel === "Heute";
              const isTomorrow = dateLabel === "Morgen";

              return (
                <div
                  key={appt.id}
                  className="group flex items-start gap-2.5 py-1.5 px-2 rounded-sm hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate("/app/terminplaner")}
                  data-testid={`appointment-item-${appt.id}`}
                >
                  {/* Date badge */}
                  <div
                    className="shrink-0 flex flex-col items-center justify-center w-11 py-1 rounded-sm text-center"
                    style={{
                      backgroundColor: isToday
                        ? "#EFF6FF"
                        : isTomorrow
                        ? "#F0FDF4"
                        : "#F9FAFB",
                      border: `1px solid ${
                        isToday ? "#BFDBFE" : isTomorrow ? "#BBF7D0" : "#E5E7EB"
                      }`,
                    }}
                  >
                    <span
                      className="text-[10px] font-semibold leading-none"
                      style={{
                        color: isToday
                          ? "#2563EB"
                          : isTomorrow
                          ? "#16A34A"
                          : "#6B7280",
                      }}
                    >
                      {dateLabel}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {isQuick && (
                        <Zap className="w-3 h-3 text-amber-500 shrink-0" />
                      )}
                      <span className="text-[12px] font-medium text-gray-800 truncate">
                        {appt.title}
                      </span>
                      {isShared ? (
                        <Share2 className="w-2.5 h-2.5 text-blue-400 shrink-0" />
                      ) : (
                        <Lock className="w-2.5 h-2.5 text-gray-300 shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {appt.time && (
                        <span className="flex items-center gap-0.5 text-[11px] text-gray-500">
                          <Clock className="w-2.5 h-2.5" />
                          {appt.time}
                          {appt.end_time && ` - ${appt.end_time}`}
                        </span>
                      )}
                      {appt.location && (
                        <span className="flex items-center gap-0.5 text-[11px] text-gray-500 truncate">
                          <MapPin className="w-2.5 h-2.5" />
                          {appt.location}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
